import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useCVDownload } from "../hooks/useCVDownload";
import { useTheme } from "../contexts/ThemeContext";

const Navbar = () => {
  const [sticky, setSticky] = useState(false);
  const [open, setOpen] = useState(false);
  const { handleDownloadCV, hasActiveCV } = useCVDownload();
  const { theme, toggleTheme } = useTheme();
  const menuLinks = [
    { name: "HOME", link: "#home" },
    { name: "ABOUT", link: "#about" },
    { name: "PROJECTS", link: "#projects" },
    { name: "RESEARCH", link: "#research" },
    { name: "SERVICES", link: "#services" },
    { name: "IMPACT", link: "#impact" },
    { name: "CONTACT", link: "#contact" },
  ];
  useEffect(() => {
    const handleScroll = () => {
      window.scrollY > 0 ? setSticky(true) : setSticky(false);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <motion.nav
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, type: "spring", stiffness: 80 }}
      className={`fixed w-full left-0 top-0 z-[999] transition-all duration-300 ${
        sticky ? "bg-slate-950/90 backdrop-blur-xl shadow-lg shadow-cyan-500/5" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-8 py-3">
        <motion.div
          initial={{ x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.7 }}
        >
          <a href="#home" className="text-xl font-bold tracking-tight select-none">
            <span className="text-white">Obert</span>
            <span className="gradient-text"> Manyasa</span>
          </a>
        </motion.div>
        <motion.div
          className="hidden md:flex items-center gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ul className="flex items-center gap-1 py-2 text-sm">
            {menuLinks?.map((menu, i) => (
              <motion.li
                key={i}
                className="px-2.5 relative group cursor-pointer"
                whileHover={{ y: -1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <a
                  href={menu?.link}
                  className="text-slate-400 hover:text-white transition-colors duration-200 text-xs font-mono tracking-wider"
                >
                  {menu?.name}
                  <motion.span
                    className="block h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"
                  />
                </a>
              </motion.li>
            ))}
            {hasActiveCV && (
              <motion.li whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
                <motion.button
                  onClick={handleDownloadCV}
                  className="ml-3 px-5 py-2 text-xs font-mono tracking-wider rounded-full border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10 transition-all duration-200"
                >
                  CV
                </motion.button>
              </motion.li>
            )}
            <motion.li whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <button
                onClick={toggleTheme}
                className="ml-2 w-9 h-9 flex items-center justify-center rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-all duration-200 text-base"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
            </motion.li>
            <motion.li whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
              <Link
                to="/admin"
                className="ml-2 px-5 py-2 text-xs font-mono tracking-wider rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-all duration-200"
              >
                Admin
              </Link>
            </motion.li>
          </ul>
        </motion.div>
        {/* Mobile menu icon */}
        <motion.div
          onClick={() => setOpen(!open)}
          className={`z-[999] text-2xl md:hidden cursor-pointer ${open ? "text-white" : "text-slate-300"}`}
          whileTap={{ scale: 0.9, rotate: 20 }}
        >
          <ion-icon name={open ? "close" : "menu"}></ion-icon>
        </motion.div>
        {/* Animated mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              key="mobile-menu"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="md:hidden text-white absolute w-3/4 h-screen px-7 py-2 font-medium bg-slate-900/95 backdrop-blur-xl top-0 right-0 shadow-2xl z-[998] border-l border-slate-800"
              style={{ borderTopLeftRadius: "2rem", borderBottomLeftRadius: "2rem" }}
            >
              <ul className="flex flex-col justify-center h-full gap-8 py-2 text-base">
                {menuLinks?.map((menu, i) => (
                  <motion.li
                    onClick={() => setOpen(false)}
                    key={i}
                    className="px-6 hover:text-cyan-400 cursor-pointer"
                    whileHover={{ scale: 1.05, x: 8 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <a href={menu?.link} className="font-mono tracking-wider text-sm">{menu?.name}</a>
                  </motion.li>
                ))}
                <li className="px-6 mt-4 flex gap-2">
                  {hasActiveCV && (
                    <motion.button
                      onClick={handleDownloadCV}
                      className="flex-1 px-5 py-3 text-xs font-mono tracking-wider rounded-full border border-cyan-500/50 text-cyan-300"
                    >
                      Download CV
                    </motion.button>
                  )}
                  <motion.button
                    onClick={toggleTheme}
                    className="w-12 h-11 flex items-center justify-center rounded-full border border-slate-700 text-slate-400 text-base"
                    whileTap={{ scale: 0.9 }}
                  >
                    {theme === 'dark' ? '☀️' : '🌙'}
                  </motion.button>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
